/**
 * A course's generated signature: which hue and which motif it draws with.
 *
 * Deterministic from the course code alone, so the same course looks the same
 * on every page, every session, and both tenants' layouts, with no stored image
 * and no instructor art direction. The subject prefix ("ARB" of "ARB 202")
 * picks the hue and the whole code picks the motif, so two courses in one
 * subject read as a family and still differ in shape (ADR 0035).
 *
 * This is presentation identity, not an academic fact. It ranks, filters, and
 * claims nothing, and the rendered mark is decorative: the code and title stay
 * the accessible identity.
 */

export const COURSE_IDENTITY_HUES = 6;
export const COURSE_SIGNATURE_MOTIFS = 6;

/** The six stroke motifs, by index: the glyph and the cover art draw the same one. */
export const COURSE_MOTIFS = ["arc", "diagonals", "lattice", "bars", "ring", "diamond"] as const;
export type CourseMotif = (typeof COURSE_MOTIFS)[number];

export interface CourseSignatureSpec {
  /** 1-based, matching the `--course-identity-N` semantic roles. */
  hue: number;
  /** 0-based index into `COURSE_MOTIFS`. */
  motif: number;
}

/** FNV-1a, 32-bit. Stable across engines and cheap enough for every render. */
function hash(text: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}

/** The leading letters of a code, or the whole code when it has none. */
export function courseSubject(code: string): string {
  const normalized = code.trim().toUpperCase();
  return normalized.match(/^[A-Z]+/)?.[0] ?? normalized;
}

/**
 * The motif steps with the course number, offset by the subject, so courses
 * in one subject whose numbers differ by less than the motif count can never
 * share a shape. A whole-code hash gave TDAY-101 and TDAY-202 identical tiles.
 * A code with no number falls back to the hash.
 */
export function courseSignature(code: string): CourseSignatureSpec {
  const normalized = code.trim().toUpperCase().replace(/[\s_-]+/g, " ");
  const subjectHash = hash(courseSubject(normalized));
  const number = normalized.match(/\d+/)?.[0];
  return {
    hue: (subjectHash % COURSE_IDENTITY_HUES) + 1,
    motif: number === undefined
      ? hash(normalized) % COURSE_SIGNATURE_MOTIFS
      : (Number(number) + subjectHash) % COURSE_SIGNATURE_MOTIFS,
  };
}

/** Spelled out rather than interpolated, so the token audit can resolve each one. */
export const COURSE_HUE: Record<number, string> = {
  1: "var(--course-identity-1)",
  2: "var(--course-identity-2)",
  3: "var(--course-identity-3)",
  4: "var(--course-identity-4)",
  5: "var(--course-identity-5)",
  6: "var(--course-identity-6)",
};

/** The tint under each hue: the cover's plane and the mark's fill. */
export const COURSE_TINT: Record<number, string> = {
  1: "var(--course-identity-1-tint)",
  2: "var(--course-identity-2-tint)",
  3: "var(--course-identity-3-tint)",
  4: "var(--course-identity-4-tint)",
  5: "var(--course-identity-5-tint)",
  6: "var(--course-identity-6-tint)",
};
